
export default function AudioServices() {
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const services = [
    {
      icon: 'ri-speaker-2-line',
      title: 'Speaker Installation & Upgrades',
      description:
        'Replace factory speakers with high-performance components for richer, fuller sound throughout your vehicle.',
    },
    {
      icon: 'ri-equalizer-line',
      title: 'Amplifier Installation',
      description:
        'Power your audio system with professionally installed amplifiers for clean, distortion-free volume at any level.',
    },
    {
      icon: 'ri-bass-line',
      title: 'Subwoofer Installation',
      description:
        'Add deep, powerful bass with custom subwoofer setups tailored to your vehicle and listening preferences.',
    },
    {
      icon: 'ri-dashboard-3-line',
      title: 'Head Unit Installation',
      description:
        'Upgrade to a modern head unit with touchscreen, Bluetooth, Apple CarPlay, Android Auto, and more.',
    },
    {
      icon: 'ri-surround-sound-line',
      title: 'Full Custom Audio Systems',
      description:
        'Complete audio system design and installation from the ground up — speakers, amps, subs, wiring, and tuning.',
    },
    {
      icon: 'ri-settings-4-line',
      title: 'Audio Diagnostics & Optimization',
      description:
        'Troubleshoot audio issues and fine-tune your existing system for optimal clarity, balance, and performance.',
    },
  ];

  return (
    <section id="audio-services" className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 bg-[#0B0B0B]">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 bg-[#2563EB]/10 border border-[#2563EB]/20 rounded-full px-5 py-2 mb-6">
            <i className="ri-volume-up-line text-[#2563EB] text-sm"></i>
            <span className="text-[#2563EB] text-xs font-semibold uppercase tracking-widest">Car Stereo &amp; Audio</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-6">
            Professional Car Stereo &amp; Audio Installation
          </h2>
          <p className="text-base sm:text-lg text-gray-400 max-w-3xl mx-auto px-4 leading-relaxed">
            Upgrade your driving experience with precision-installed, high-performance audio systems.
          </p>
        </div>

        <div className="max-w-4xl mx-auto mb-10 sm:mb-14">
          <p className="text-sm sm:text-base text-gray-500 text-center leading-relaxed px-4">
            The Auto Salon specializes in professional stereo installation, speaker upgrades, amplifier installation, and full audio system integration. Whether you&apos;re looking for deeper bass, clearer sound, or a complete custom system, every installation is performed with precision, clean wiring, and factory-level fit and finish.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 mb-12 sm:mb-16">
          {services.map((service, index) => (
            <div
              key={index}
              className="group bg-[#111827] border border-gray-800 rounded-2xl p-6 sm:p-8 hover:border-[#2563EB]/40 hover:shadow-[0_0_30px_rgba(37,99,235,0.08)] transition-all duration-300"
            >
              <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center bg-[#2563EB]/10 border border-[#2563EB]/20 rounded-xl mb-5 sm:mb-6 group-hover:bg-[#2563EB]/20 transition-colors duration-300">
                <i className={`${service.icon} text-[#2563EB] text-xl sm:text-2xl`}></i>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-white mb-3">
                {service.title}
              </h3>
              <p className="text-sm text-gray-400 leading-relaxed">
                {service.description}
              </p>
            </div>
          ))}
        </div>

        <div className="relative rounded-2xl overflow-hidden">
          <div className="w-full h-64 sm:h-80">
            <img
              src="https://public.readdy.ai/ai/img_res/c610e431ce628bd3298f15d0cfe5596c.jpg"
              alt="Professional Car Audio Installation"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0B0B0B] via-[#0B0B0B]/70 to-transparent"></div>
          </div>
          <div className="absolute inset-0 flex items-center">
            <div className="px-8 sm:px-12 max-w-xl">
              <h3 className="text-2xl sm:text-3xl font-bold text-white mb-3 sm:mb-4">
                Ready for Premium Sound?
              </h3>
              <p className="text-sm sm:text-base text-gray-300 mb-6 sm:mb-8 leading-relaxed">
                Transform your daily commute into an immersive audio experience. Contact us for a free consultation.
              </p>
              <button
                onClick={() => scrollToSection('contact')}
                className="bg-[#2563EB] text-white px-8 sm:px-10 py-3 sm:py-4 rounded-full text-sm sm:text-base font-medium hover:bg-[#1d4ed8] hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Upgrade Your Audio System
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
